/*
 * Code generated by Speakeasy (https://speakeasy.com). DO NOT EDIT.
 */

import {
  InvalidateQueryFilters,
  QueryClient,
  useQuery,
  UseQueryResult,
  useSuspenseQuery,
  UseSuspenseQueryResult,
} from "@tanstack/react-query";
import { GramError } from "../models/errors/gramerror.js";
import {
  ConnectionError,
  InvalidRequestError,
  RequestAbortedError,
  RequestTimeoutError,
  UnexpectedClientError,
} from "../models/errors/httpclienterrors.js";
import { ResponseValidationError } from "../models/errors/responsevalidationerror.js";
import { SDKValidationError } from "../models/errors/sdkvalidationerror.js";
import { ServiceError } from "../models/errors/serviceerror.js";
import {
  ListDirectoryRoleMappingsRequest,
  ListDirectoryRoleMappingsSecurity,
} from "../models/operations/listdirectoryrolemappings.js";
import { useGramContext } from "./_context.js";
import {
  QueryHookOptions,
  SuspenseQueryHookOptions,
  TupleToPrefixes,
} from "./_types.js";
import {
  buildDirectoryRoleMappingsQuery,
  DirectoryRoleMappingsQueryData,
  prefetchDirectoryRoleMappings,
  queryKeyDirectoryRoleMappings,
} from "./directoryRoleMappings.core.js";
export {
  buildDirectoryRoleMappingsQuery,
  type DirectoryRoleMappingsQueryData,
  prefetchDirectoryRoleMappings,
  queryKeyDirectoryRoleMappings,
};

export type DirectoryRoleMappingsQueryError =
  | ServiceError
  | GramError
  | ResponseValidationError
  | ConnectionError
  | RequestAbortedError
  | RequestTimeoutError
  | InvalidRequestError
  | UnexpectedClientError
  | SDKValidationError;

/**
 * listDirectoryRoleMappings access
 *
 * @remarks
 * List the organization's directory groups and attribute values, and the roles mapped to them.
 */
export function useDirectoryRoleMappings(
  request?: ListDirectoryRoleMappingsRequest | undefined,
  security?: ListDirectoryRoleMappingsSecurity | undefined,
  options?: QueryHookOptions<
    DirectoryRoleMappingsQueryData,
    DirectoryRoleMappingsQueryError
  >,
): UseQueryResult<
  DirectoryRoleMappingsQueryData,
  DirectoryRoleMappingsQueryError
> {
  const client = useGramContext();
  return useQuery({
    ...buildDirectoryRoleMappingsQuery(
      client,
      request,
      security,
      options,
    ),
    ...options,
  });
}

/**
 * listDirectoryRoleMappings access
 *
 * @remarks
 * List the organization's directory groups and attribute values, and the roles mapped to them.
 */
export function useDirectoryRoleMappingsSuspense(
  request?: ListDirectoryRoleMappingsRequest | undefined,
  security?: ListDirectoryRoleMappingsSecurity | undefined,
  options?: SuspenseQueryHookOptions<
    DirectoryRoleMappingsQueryData,
    DirectoryRoleMappingsQueryError
  >,
): UseSuspenseQueryResult<
  DirectoryRoleMappingsQueryData,
  DirectoryRoleMappingsQueryError
> {
  const client = useGramContext();
  return useSuspenseQuery({
    ...buildDirectoryRoleMappingsQuery(
      client,
      request,
      security,
      options,
    ),
    ...options,
  });
}

export function setDirectoryRoleMappingsData(
  client: QueryClient,
  queryKeyBase: [
    parameters: {
      gramKey?: string | undefined;
      gramSession?: string | undefined;
    },
  ],
  data: DirectoryRoleMappingsQueryData,
): DirectoryRoleMappingsQueryData | undefined {
  const key = queryKeyDirectoryRoleMappings(...queryKeyBase);

  return client.setQueryData<DirectoryRoleMappingsQueryData>(key, data);
}

export function invalidateDirectoryRoleMappings(
  client: QueryClient,
  queryKeyBase: TupleToPrefixes<
    [parameters: {
      gramKey?: string | undefined;
      gramSession?: string | undefined;
    }]
  >,
  filters?: Omit<InvalidateQueryFilters, "queryKey" | "predicate" | "exact">,
): Promise<void> {
  return client.invalidateQueries({
    ...filters,
    queryKey: [
      "@gram/client",
      "access",
      "listDirectoryRoleMappings",
      ...queryKeyBase,
    ],
  });
}

export function invalidateAllDirectoryRoleMappings(
  client: QueryClient,
  filters?: Omit<InvalidateQueryFilters, "queryKey" | "predicate" | "exact">,
): Promise<void> {
  return client.invalidateQueries({
    ...filters,
    queryKey: ["@gram/client", "access", "listDirectoryRoleMappings"],
  });
}
